import { useState, useCallback } from "react";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import useMessageDialog from "@util/hook/useMessageDialog";
import { isEmpty } from "@util/utils";

/**
 * 管理會員結帳資訊的 Hook
 *
 * @returns {Object} 結帳資訊資料和方法
 */
const useMemberCheckoutInfo = () => {
	const { getOrderCheckoutApi } = useOrderCheckoutApi();
	const setMessageDialog = useMessageDialog();

	// 結帳資訊完整資料
	const [checkoutInfo, setCheckoutInfo] = useState({});

	// 從 checkoutInfo 提取常用資料，方便使用
	const {
		unpaidOrders = [], // 賒帳訂單清單
		invoiceSetting = {}, // 會員發票預設值（已先設定）
		member = {}, // 會員資料
		payableTotalPrice = 0, // 欠費訂單應付總金額
		paidTotalPrice = 0, // 欠費訂單已付總金額
		temporaryWalletPrice = 0, // 暫收款金額
		checkUsingDeviceOrder = false, // 是否有訂閱智能系統
	} = checkoutInfo;

	/**
	 * 取得會員結帳資訊
	 * 根據會員狀態取得結帳前的準備資訊
	 * @param {string|number} memberID - 會員 ID
	 * @returns {Promise<Object|null>} 結帳資訊資料
	 */
	const fetchCheckoutInfo = useCallback(
		async memberID => {
			if (!memberID) {
				// 清除資料
				setCheckoutInfo({});
				return null;
			}

			try {
				const resp = await getOrderCheckoutApi(memberID);
				if (resp) {
					// 將後端回傳的 orders 映射為 unpaidOrders（向後兼容）
					const { orders, ...other } = resp;
					const normalizedResp = { ...other, unpaidOrders: orders || resp.unpaidOrders || [] };

					// 先設置不包含賒帳訂單的資料（預設不顯示賒帳資訊）
					const { unpaidOrders: _, ...otherWithoutOrders } = normalizedResp;
					setCheckoutInfo(otherWithoutOrders);

					// 若有賒帳訂單，跳出提醒視窗
					if (!isEmpty(normalizedResp.unpaidOrders)) {
						setMessageDialog({
							open: true,
							title: "欠費提醒",
							msg: "查詢到會員尚有未結清的訂單，請問是否於本次消費一起結帳？",
							onConfirm: () => {
								// 用戶選擇結清：設置完整的結帳資訊（包含賒帳訂單）
								setCheckoutInfo(normalizedResp);
							},
						});
					}
					return normalizedResp;
				}
			} catch (error) {
				console.error("取得會員結帳資訊失敗:", error);
			}
			return null;
		},
		// eslint-disable-next-line
		[getOrderCheckoutApi, setMessageDialog],
	);

	/**
	 * 清除結帳資訊資料
	 */
	const clearCheckoutInfo = useCallback(
		() => {
			setCheckoutInfo({});
		},
		// eslint-disable-next-line
		[],
	);

	return {
		// 完整資訊資料
		checkoutInfo,

		// 提取的常用資料（方便使用）
		unpaidOrders,
		invoiceSetting,
		member,
		payableTotalPrice,
		paidTotalPrice,
		temporaryWalletPrice,
		checkUsingDeviceOrder,

		// 方法
		fetchCheckoutInfo,
		clearCheckoutInfo,
	};
};

export default useMemberCheckoutInfo;
